import { FC, useEffect, useMemo, useState } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import { DateFormat, getNiceDate, ViewState } from '@/lib/helpers';
import useQueryHelper from '@/hooks/use-query-helper';
import useFormHandle from '@/hooks/use-form-handle.hook';
import ReadonlyView from '../../form/readonly-view';
import InputControl from '../../form/input-control';
import useToast from '@/hooks/use-toast.hook';
import useModal from '@/hooks/use-modal.hook';
import DrawerButtons from '@/components/form/drawer-buttons';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import {
  OrgCreateFormValues,
  OrgMode,
  OrgModel,
  OrgModeOptions,
  OrgStatusOptions,
  DemoAdminPasswordRule,
} from '@/lib/models/org.model';
import { createOrg, deleteOrg, getOrgById, patchOrg } from '@/lib/services/org.service';
import DrawerMenu from '../menu';
import UpdateMenuItems from '../menu/update-menu-items';
import { Box, Grid, Stack } from '@mui/material';
import Chip from '@/components/chip';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import { getAllPartners, getPartner, getPartnerById } from '@/lib/services/partner.service';
import useApp from '@/hooks/use-app.hook';
import { userCan } from '@/lib/services/auth.service';
import { PartnerModel } from '@/lib/models/partner.model';
import TextInput from '@/components/form/text-input';
import { DrawerFormSection } from '../form-section';
import Text from '@/components/text';
import InlineToast from '@/components/toasts/inline';
import NoneChip from '@/components/chip/none-chip';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import EntitlementCard from '@/components/entitlement-card';
import { FieldError } from '@/lib/services';
import { DrawerProps } from '../helpers';
import { getEmailDomainOptions } from '@/components/form/email-with-domain/helpers';
import PolicyEngineCard from './policy-engine';

interface Props extends DrawerProps {
  id: string | null;
}

const fields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  { label: 'Domains', prop: 'domains' },
  { label: 'Partner', prop: 'partner' },
  { label: 'Status', prop: 'status' },
  { label: 'Mode', prop: 'mode' },
  { label: 'Contact', prop: 'contact' },
  { label: 'Prompt Retention', prop: 'disablePromptRetention' },
  { label: '', prop: 'policyEngine' },
  { label: 'Usage', prop: 'usage', propType: 'section' },
  { label: '', prop: 'entitlement' },
];

// Every field path the edit form can render inline; API errors on other paths need a toast.
const ShownFieldPaths = new Set([
  'name',
  'domain',
  'domains',
  'partnerId',
  'createAdminUser',
  'contact.name',
  'contact.email',
  'demoAdminPassword',
  'disablePromptRetention',
  'mode',
  'status',
  'startDate',
  'term',
]);

const formModel: OrgModel = new OrgModel({
  status: 'active',
  mode: 'platform',
  term: 30,
  startDate: getNiceDate(new Date(), DateFormat.NOTIME).formatted,
  disablePromptRetention: false,
});

const getReadonlyData = (
  data: OrgModel | null,
  partner: PartnerModel | null,
  isSuper: boolean,
  onPolicyEngineChange: () => void
) => {
  if (!data) {
    return {};
  }

  const { name, domains, status, mode, contact, disablePromptRetention } = data;

  return {
    name,
    domains: (
      <Stack gap={0.5} direction="row">
        {domains.map((domain) => (
          <Chip key={domain} label={domain} />
        ))}
      </Stack>
    ),
    partner: partner?.name,
    status: <StatusChip value={status as ChipStatus} />,
    mode: OrgModeOptions.find(({ value }) => value === mode)?.label || <NoneChip notAvailable />,
    contact: (
      <Stack gap={3} mt={2}>
        <TextInput
          label="Name"
          name="name"
          value={contact.name || 'n/a'}
          readonly
          copyClip={!!contact.name}
          size="small"
        />
        <TextInput
          label="Email"
          name="email"
          value={contact.email || 'n/a'}
          readonly
          copyClip={!!contact.email}
          size="small"
        />
      </Stack>
    ),
    entitlement: <EntitlementCard org={data} />,
    disablePromptRetention: <StatusChip value={disablePromptRetention ? 'disabled' : 'enabled'} />,
    ...(isSuper &&
      data.id && { policyEngine: <PolicyEngineCard org={data} onChange={onPolicyEngineChange} /> }),
  };
};

const getReadonlyFields = (partner: PartnerModel | null, isSuper: boolean) => {
  const compFields = [...fields];

  if (!partner) {
    const partnerFieldIndex = compFields.findIndex(({ prop }) => prop === 'partner');
    compFields.splice(partnerFieldIndex, 1);
  }

  if (!isSuper) {
    const policyEngineFieldIndex = compFields.findIndex(({ prop }) => prop === 'policyEngine');
    compFields.splice(policyEngineFieldIndex, 1);
  }

  return compFields;
};

const OrgDrawer: FC<Props> = ({ id, open, onClose, onChange, className, isTopDrawer }) => {
  const [orgId, setOrgId] = useState(id || null);
  const isNew = !orgId;
  const [mode, setMode] = useState<ViewState>(isNew ? 'add' : 'view');
  // Locked field always shows the org's current partner.
  const [partnerLocked, setPartnerLocked] = useState(true);
  const { toast, errorToast } = useToast();
  const { openModal } = useModal();

  const { user } = useApp();
  const isSuper = userCan(user, 'super');

  if (!isSuper) {
    formModel.partnerId = user!.partnerId;
  }

  const query = useQuery<[OrgModel | null, PartnerModel | null]>(
    [QueryKey.OrgView, orgId, isSuper],
    async () => {
      const org = await getOrgById(orgId!);

      if (!org) {
        return [null, null];
      }

      if (!isSuper) {
        return [org, null];
      }

      const partner = org.partnerId ? await getPartnerById(org.partnerId) : new PartnerModel();
      return [org, partner];
    },
    {
      enabled: !isNew,
    }
  );

  const { showChildren, showLoader } = useQueryHelper(query);
  const { data, refetch } = query;
  const [org, partner] = data || [new OrgModel(), null];

  const partnersQuery = useQuery([QueryKey.PartnerList], async () => getAllPartners(), {
    enabled: isSuper,
  });
  const { data: partners } = partnersQuery;
  const { showLoader: partnersLoading } = useQueryHelper(partnersQuery);

  const partnerOpts = (partners || []).map(({ id, name }) => ({ label: name, value: id }));

  // A partner user may only create orgs in the modes its partner allows; root sees every mode.
  const ownPartnerQuery = useQuery([QueryKey.PartnerProfile], async () => getPartner(), {
    enabled: !isSuper,
  });
  const ownPartner = ownPartnerQuery.data;
  const modeOptions = useMemo(
    () => (isSuper ? OrgModeOptions : ownPartner?.modeOptionsFor('create') || []),
    [isSuper, ownPartner]
  );
  // Until the partner has loaded there are no options; once it has, an empty list means it may create nothing.
  const canCreateAnyMode = isSuper || modeOptions.length > 0;
  const partnerCannotCreate = !isSuper && isNew && !!ownPartner && modeOptions.length === 0;
  // Root may do anything; a partner user is held to its partner's per-mode actions, which the API enforces too.
  const partnerMay = (action: 'update' | 'delete') =>
    isSuper || (!!org?.id && !!ownPartner?.mayAct(org.mode, action));

  const formHandle = useFormHandle<OrgCreateFormValues>({
    initialValues: {
      ...formModel,
      createAdminUser: true,
      demoAdminPassword: '',
    } as OrgCreateFormValues,
    validationSchema: isNew ? formModel.createSchema : formModel.schema,
    validateOnChange: !isNew,
    onSubmit: async (org, { setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        const domainErrors = fieldErrors.filter(({ path }) =>
          String(path).match(/^domains\[[0-9]+\]/)
        );

        if (domainErrors.length) {
          fieldErrors.push({
            path: 'domain',
            msg: domainErrors.map(({ msg }) => msg).join(', '),
            value: '',
          });
        }

        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));

        // An error on a field the form does not show would otherwise be silent, so only
        // those become a toast; errors on shown fields already appear inline.
        const hiddenErrors = fieldErrors.filter(
          ({ path }) =>
            !ShownFieldPaths.has(String(path)) && !String(path).match(/^domains\[[0-9]+\]/)
        );

        if (hiddenErrors.length) {
          errorToast(hiddenErrors.map(({ msg }) => msg).join(' '));
        }
      };

      if (!orgId) {
        const response = await createOrg(org, isSuper);

        const { created, model: newOrg, error, fieldErrors } = response;

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        if (error || !created) {
          errorToast(error || 'The organization was not created');
          return;
        }

        toast('The organization was created');
        setOrgId(newOrg!.id);
        onChange && onChange();
        setMode('view');

        return;
      }
      const { patched, error, fieldErrors } = await patchOrg(org.id, org, isSuper);

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      if (error || !patched) {
        errorToast(error || 'The organization was not updated');
        return;
      }

      toast('The organization was updated');
      refetch();
      onChange && onChange();
      setMode('view');
    },
  });

  const { handleSubmit, canSubmit, isSubmitting, loadData, setFieldValue, values } = formHandle;

  const inWriteMode = ['edit', 'add'].includes(mode);

  const handleCancel = () => {
    if (isNew) {
      onClose();
      return;
    }
    setMode('view');
    loadData(org!);
  };

  const handleEdit = () => {
    setPartnerLocked(true);
    setMode('edit');
  };

  // Relocking discards any partner change, so a locked field always shows the org's current partner.
  const handleTogglePartnerLock = () => {
    if (!partnerLocked) {
      setFieldValue('partnerId', org!.partnerId);
    }
    setPartnerLocked(!partnerLocked);
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: (
        <Box>
          Are you sure you want to delete{' '}
          <Text bold inline>
            {org!.name}
          </Text>
          ?
          <Box mt={4}>
            <InlineToast
              level="warning"
              message="All data owned by this organization will be permanently deleted, including users and
              user events, private models, datasources, and connectors."
            />
          </Box>
        </Box>
      ),
      onClose: (confirm: boolean) => {
        if (confirm) {
          deleteOrg(orgId!).then(({ deleted, error }) => {
            if (deleted) {
              setOrgId('');
              onClose();
              toast('The organization was deleted');
              onChange && onChange();
              return;
            }

            errorToast(error || 'The organization was not deleted');
          });
        }
      },
    });
  };

  const handlePolicyEngineChange = () => {
    refetch();
    onChange && onChange();
  };

  const handleReadonlyData = (data: Record<string, unknown>) => {
    return {
      ...data,
    };
  };

  // The form defaults to platform. A partner that may not create platform orgs starts on its first
  // allowed mode. Set as the form's starting value, not as an edit: a fresh form must not read as
  // dirty, which would enable Submit and lock navigation before anything was typed.
  useEffect(() => {
    if (!isNew || !modeOptions.length || modeOptions.some(({ value }) => value === values.mode)) {
      return;
    }
    const first = modeOptions[0].value as OrgMode;
    loadData({ ...values, mode: first });
  }, [isNew, modeOptions, values, loadData]);

  useEffect(() => {
    if (isNew) {
      const spPartner = partners?.find(({ domains }) => domains.includes('surepath.ai'));
      if (spPartner) {
        setFieldValue('partnerId', spPartner.id);
      }
    }
  }, [partners, isNew, setFieldValue]);

  const canDelete = !!org?.id && ['error', 'inactive'].includes(org.status) && partnerMay('delete');
  const orgEditable =
    !!org?.id && !['creating', 'deleting'].includes(org.status) && partnerMay('update');
  const canEdit = !inWriteMode && orgEditable;
  const partnerChanged = !isNew && !!org?.id && values.partnerId !== org.partnerId;
  // A demo org's contact is its owner and the account its password was set on, so it is fixed after create.
  const contactLocked = !isNew && values.mode === 'demo';
  const canChangeStatus = ['active', 'inactive'].includes(values.status);
  const singleDomain = values.domain;
  const allDomains = [...values.domains, ...(singleDomain ? [singleDomain] : [])];
  const emailDomainOptions = getEmailDomainOptions(allDomains);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className={className}
      loading={showLoader || partnersLoading}
    >
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={
          !isNew ? (
            <DrawerMenu>
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={partnerMay('delete') ? handleDelete : undefined}
                canDelete={canDelete}
                canEdit={canEdit}
                editSubtitle={
                  orgEditable
                    ? ''
                    : partnerMay('update')
                      ? 'You cannot edit an organization that is creating or deleting'
                      : 'Your partner cannot update organizations of this mode'
                }
                deleteSubtitle="An organization must be inactive to be deleted"
              />
            </DrawerMenu>
          ) : undefined
        }
      >
        {isNew ? 'Add Organization' : org!.name}
      </DrawerHeader>
      <DrawerContent>
        {showChildren && (
          <Box pt={2}>
            {!inWriteMode ? (
              <ReadonlyView
                fields={getReadonlyFields(partner, isSuper)}
                data={getReadonlyData(org, partner, isSuper, handlePolicyEngineChange)}
                onData={handleReadonlyData}
              />
            ) : (
              <Form formHandle={formHandle} query={query}>
                <DrawerFormSection label="Profile" />
                <Box mb={4}>
                  <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

                  <Box mt={1} mb={1}>
                    {isNew && (
                      <InputControl
                        name="domains"
                        label="Domain"
                        type="array-single"
                        formHandle={formHandle}
                      />
                    )}

                    {!isNew && (
                      <InputControl
                        name="domains"
                        label="Domains"
                        formHandle={formHandle}
                        type="list-editor"
                        height={90}
                        required
                      />
                    )}
                  </Box>

                  {isSuper && (
                    <Stack direction="row" gap={1} alignItems="center">
                      <Box flex={1}>
                        <InputControl
                          type="select"
                          name="partnerId"
                          label="Partner"
                          formHandle={formHandle}
                          options={partnerOpts}
                          disabled={!isNew && partnerLocked}
                        />
                      </Box>
                      {!isNew && (
                        <Tooltip
                          title={
                            partnerLocked
                              ? 'Unlock to move this organization to a different partner'
                              : 'Lock and keep the current partner'
                          }
                        >
                          <Box display="flex" mt={1}>
                            <Icon
                              name={partnerLocked ? 'lock' : 'unlock'}
                              onClick={handleTogglePartnerLock}
                              color="grey"
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                  {isSuper && partnerChanged && (
                    <Box mt={1}>
                      <InlineToast
                        level="warning"
                        message="Saving will move this organization to the selected partner. Its users, data, and settings stay the same; only the managing partner changes."
                      />
                    </Box>
                  )}
                </Box>

                <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
                  <DrawerFormSection label="Contact Info" />
                  {isNew && values.mode !== 'demo' && (
                    <Box maxWidth={300}>
                      <InputControl
                        type="switch"
                        name="createAdminUser"
                        label="Create Org Owner"
                        formHandle={formHandle}
                      />
                    </Box>
                  )}
                </Stack>
                <Box mb={4}>
                  <InputControl
                    name="contact.name"
                    label="Name"
                    formHandle={formHandle}
                    disabled={contactLocked}
                  />
                  <InputControl
                    name="contact.email"
                    label="Email"
                    type="email-with-domain"
                    formHandle={formHandle}
                    options={emailDomainOptions}
                    disabled={contactLocked}
                  />
                  {isNew && values.mode === 'demo' && (
                    <>
                      <InputControl
                        type="password"
                        name="demoAdminPassword"
                        label="Demo admin password"
                        formHandle={formHandle}
                        autoComplete="new-password"
                      />
                      <Text size="small" color="grey">
                        The contact above becomes the organization owner and signs in with this
                        password. {DemoAdminPasswordRule}.
                      </Text>
                    </>
                  )}
                </Box>

                <DrawerFormSection label="Settings" />
                <Box mb={4}>
                  <InputControl
                    type="switch"
                    name="disablePromptRetention"
                    label="Prompt Retention"
                    formHandle={formHandle}
                    invert
                  />
                </Box>

                <DrawerFormSection label="Entitlement" />
                <Box mb={4}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <InputControl
                        type="select"
                        name="mode"
                        label="Mode"
                        formHandle={formHandle}
                        // Locked on an existing org, so it must be able to show any mode the org
                        // holds, including ones the partner may not create.
                        options={isNew ? modeOptions : OrgModeOptions}
                        disabled={!isNew}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      {canChangeStatus && (
                        <InputControl
                          type="select"
                          name="status"
                          label="Status"
                          formHandle={formHandle}
                          options={OrgStatusOptions}
                        />
                      )}
                      {!canChangeStatus && (
                        <TextInput
                          name="status"
                          label="Status"
                          value={String(values.status).toUpperCase()}
                          readonly
                        />
                      )}
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <InputControl
                        type="date"
                        name="startDate"
                        label="Start Date"
                        formHandle={formHandle}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <InputControl
                        name="term"
                        label="Term (months)"
                        type="number"
                        formHandle={formHandle}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {partnerCannotCreate && (
                  <Box mb={2}>
                    <InlineToast
                      level="warning"
                      message="Your partner is not allowed to create organizations in any mode."
                    />
                  </Box>
                )}

                <DrawerButtons
                  submit
                  cancel
                  isNew={isNew}
                  canSubmit={canSubmit && (!isNew || canCreateAnyMode)}
                  canCancel={!isSubmitting}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                />
              </Form>
            )}
          </Box>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default OrgDrawer;
